import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { selectCompanyId, selectUserToken } from '@rpt-ngrx/selectors/session.selector';
import { WINDOW } from '@services/window';

interface IRequestOptions {
    headers?: HttpHeaders | Record<string, string | string[]>;
    observe?: any;
    params?: HttpParams | Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>>;
    reportProgress?: boolean;
    responseType?: any;
    withCredentials?: boolean;
    body?: any;
    context?: HttpContext;
}

@Injectable()
class CustomHttpClient {
    private token;
    private companyId;
    private api = `${environment.backendURI}/internal`;

    private token$: Observable<string>;
    private currentCompanyId$: Observable<string>;

    constructor(
        public http: HttpClient,
        private store: Store,
        @Inject(WINDOW) private window: Window,
    ) {
        this.token$ = this.store.select(selectUserToken);
        this.currentCompanyId$ = this.store.select(selectCompanyId);
        this.token$.subscribe((token) => (this.token = token));
        this.currentCompanyId$.pipe().subscribe((id) => {
            this.companyId = id;
        });
    }

    /**
     * GET request
     * @param {string} endPoint it doesn't need / in front of the end point
     * @param {IRequestOptions} options options of the request like headers, body, etc.
     * @param {string} api use if there is needed to send request to different back-end than the default one.
     * @returns {Observable<T>}
     */
    public get<T>(endPoint: string, options?: IRequestOptions): Observable<T> {
        return this.http.get<T>(this.api + endPoint, options) as Observable<T>;
    }

    /**
     * POST request
     * @param {string} endPoint end point of the api
     * @param {Object} params body of the request.
     * @param {IRequestOptions} options options of the request like headers, body, etc.
     * @returns {Observable<T>}
     */
    public post<T>(endPoint: string, params: object, options?: IRequestOptions): Observable<T> {
        return this.http.post<T>(this.api + endPoint, params, options) as Observable<T>;
    }

    /**
     * PUT request
     * @param {string} endPoint end point of the api
     * @param {Object} params body of the request.
     * @param {IRequestOptions} options options of the request like headers, body, etc.
     * @returns {Observable<T>}
     */
    public put<T>(endPoint: string, params: object, options?: IRequestOptions): Observable<T> {
        return this.http.put<T>(this.api + endPoint, params, options) as Observable<T>;
    }

    /**
     * DELETE request
     * @param {string} endPoint end point of the api
     * @param {IRequestOptions} options options of the request like headers, body, etc.
     * @returns {Observable<T>}
     */
    public delete<T>(endPoint: string, options?: IRequestOptions): Observable<T> {
        return this.http.delete<T>(this.api + endPoint, options) as Observable<T>;
    }

    public getAccessToken() {
        return this.token;
    }

    public getCompany() {
        return this.companyId;
    }

    public getReports({ hasLiabilitySalesIntoGrossSales, hasPreTenderVoidAmounts, hasStartingAmount }) {
        const headers = new HttpHeaders({
            'x-company-id': this.companyId,
            'x-site-ids': 'All',
        });

        return this.post(
            '/reports',
            { hasLiabilitySalesIntoGrossSales, hasPreTenderVoidAmounts, hasStartingAmount },
            { headers },
        ).pipe(
            shareReplay(),
            catchError(({ error }) => {
                return error.fallbackData ? of(error.fallbackData) : of([]);
            }),
        );
    }

    public getHostname(): string {
        return this.window.location.hostname;
    }
}

function opraHttpFactory(http: HttpClient, store: Store, window: Window) {
    return new CustomHttpClient(http, store, window);
}

export { IRequestOptions, opraHttpFactory, CustomHttpClient };
